import { useEffect, useState } from "react";
import { ChevronRight, Folder, FolderUp } from "lucide-react";
import { crumbs, joinFolder, parentFolder } from "../lib/folderPath.ts";

interface Props {
  workspaceName: string;
  browse: (path: string) => Promise<{ path: string; folders: string[]; truncated: boolean }>;
  path: string;
  onPath: (path: string) => void;
}

/** Browses sub-folders INSIDE one approved Workspace. It can only ask for (Workspace, relative path); the agent enforces the rest. */
export function FolderBrowser({ workspaceName, browse, path, onPath }: Props) {
  const [folders, setFolders] = useState<string[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setFolders(null);
    browse(path)
      .then((r) => !cancelled && (setFolders(r.folders), setTruncated(r.truncated), setError(null)))
      .catch((e: unknown) => !cancelled && setError((e as Error).message));
    return () => {
      cancelled = true;
    };
  }, [browse, path]);

  return (
    <div className="folder-browser" data-testid="folder-browser">
      <div className="folder-crumbs">
        <button type="button" className="link-btn" onClick={() => onPath("")} data-testid="folder-root">
          {workspaceName}
        </button>
        {crumbs(path).map((c) => (
          <span key={c.path}>
            <ChevronRight size={12} aria-hidden />
            <button type="button" className="link-btn" onClick={() => onPath(c.path)}>
              {c.label}
            </button>
          </span>
        ))}
      </div>
      {error && <p className="msg-error">{error}</p>}
      <ul className="folder-list">
        {path !== "" && (
          <li>
            <button type="button" onClick={() => onPath(parentFolder(path))} data-testid="folder-up">
              <FolderUp size={14} aria-hidden /> ..
            </button>
          </li>
        )}
        {folders?.map((f) => (
          <li key={f}>
            <button type="button" onClick={() => onPath(joinFolder(path, f))} data-testid={`folder-${f}`}>
              <Folder size={14} aria-hidden /> {f}
            </button>
          </li>
        ))}
        {folders?.length === 0 && <li className="muted small">No sub-folders here.</li>}
        {folders === null && !error && <li className="muted small">Loading…</li>}
        {truncated && <li className="muted small">List truncated.</li>}
      </ul>
    </div>
  );
}
